import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { forEach } from 'lodash';
import { Cart, QueryParams } from '@/type';

const parseParamsToQuery = (
  params?: Record<string, string | string[] | undefined> | QueryParams
) => {
  let query = '';
  if (params) {
    const urlParams = new URLSearchParams();
    forEach(params, (value, key) => {
      if (Array.isArray(value)) {
        value.forEach((v) => urlParams.append(key, v));
      } else if (value !== undefined && value !== null) {
        urlParams.append(key, String(value));
      }
    });
    query = `?${urlParams.toString()}`;
  }
  return query;
};

export const fetchData = async (data: {
  path: string;
  token?: string;
  contentType?: string;
  params?: Record<string, string | string[] | undefined> | QueryParams;
}) => {
  const headers: Record<string, string> = {
    'Content-Type': data.contentType ?? 'application/json',
  };

  if (data.token) {
    headers.Authorization = `Bearer ${data.token}`;
  }

  const query = parseParamsToQuery(data.params);

  const response = await fetch(`${data.path}${query}`, {
    method: 'GET',
    headers: headers,
  });
  return getResponseData(response);
};

export const useFetchData = <T>(data: {
  path: string;
  token?: string;
  contentType?: string;
  params?: Record<string, string | undefined> | QueryParams;
}) => {
  return useQuery<T>({
    queryKey: ['path', data.path, 'params', data.params],
    queryFn: () => fetchData(data),
    enabled: !!data.path,
  });
};

const postData = async (data: {
  path: string;
  payload?: Record<string, string | undefined> | FormData | Cart;
  token?: string;
  method?: string;
  contentType?: string;
  params?: Record<string, string | string[] | undefined> | QueryParams;
}) => {
  const headers: Record<string, string> = {};

  if (!(data?.payload instanceof FormData)) {
    headers['Content-Type'] = data.contentType ?? 'application/json';
  }

  if (data.token) {
    headers.Authorization = `Bearer ${data.token}`;
    headers['apikey'] = data.token;
  }

  const query = parseParamsToQuery(data.params);

  const body =
    data?.payload instanceof FormData
      ? data.payload
      : data.payload
      ? JSON.stringify(data.payload)
      : undefined;

  const options = {
    method: data.method ?? 'POST',
    headers: headers,
    body: body,
  };

  const response = await fetch(`${data.path}${query}`, options);
  return getResponseData(response);
};

const getResponseData = async (response: Response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  if (response.headers.get('content-type')?.includes('application/json')) {
    return await response.json();
  } else if (response.headers.get('content-type')?.startsWith('text')) {
    return await response.text();
  } else {
    return await response.blob();
  }
};

export const useTransmitData = (option?: { reset: string[] }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postData,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['path'],
      });
      const resetPaths = option?.reset ?? [];
      forEach(resetPaths, (it) =>
        queryClient.invalidateQueries({
          queryKey: [it],
        })
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });
};

export function useLazyQuery<TData, TError, TVariables>(
  getQueryKey: (variables: TVariables) => QueryKey,
  queryFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>
) {
  const [variables, setVariables] = useState<TVariables | null>(null);

  const query = useQuery<TData, TError>({
    queryKey: variables ? getQueryKey(variables) : ['__lazy__', Symbol()],
    queryFn: variables
      ? () => queryFn(variables)
      : async () => {
          return undefined as unknown as TData;
        },
    enabled: variables !== null && variables !== undefined,
    ...options,
  });

  const execute = useCallback(
    async (vars: TVariables) => {
      setVariables(vars);
      await new Promise((r) => setTimeout(r, 0));
      const result = await query.refetch();
      return result.data;
    },
    [query]
  );

  return [execute, query] as const;
}
